<script setup lang="ts">
  import { ROUTES } from '@/router/routes'
  import { useUserStore } from '@/store/admin/user'
  import {
    ChatBubbleLeftRightIcon,
    ChevronDownIcon,
    ChevronLeftIcon,
    CreditCardIcon,
    DocumentIcon,
    DocumentTextIcon,
    EnvelopeIcon,
    HomeIcon,
    PencilSquareIcon,
    PhoneIcon,
    QuestionMarkCircleIcon,
    StarIcon,
    SwatchIcon,
    UsersIcon
  } from '@heroicons/vue/24/outline'
  import { computed, nextTick, ref, onMounted } from 'vue'
  import { useRoute } from 'vue-router'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'
  import { FlagEnum } from '@/types/generated'

  const userStore = useUserStore()
  const user = computed(() => userStore.user)

  const route = useRoute()

  const pdfFormTemplatesAdminFlag = useFeatureFlag(FlagEnum.PDF_FORM_TEMPLATES_ADMIN)

  onMounted(async () => {
    await pdfFormTemplatesAdminFlag.checkFlag()
  })

  interface Topic {
    icon: any
    title: string
    routes: Route[]
    requiredPermission?: string
    requiredFeatureFlag?: () => boolean
  }

  interface Route {
    title: string
    route: string
  }

  const topics: Topic[] = [
    {
      icon: UsersIcon,
      title: 'Customers',
      routes: [
        {
          title: 'Customers',
          route: ROUTES.ADMIN.CUSTOMERS.CUSTOMER_LIST
        },
        {
          title: 'Map',
          route: ROUTES.ADMIN.CUSTOMERS.CUSTOMER_MAP
        }
      ]
    },
    {
      icon: PhoneIcon,
      title: 'Voice',
      routes: [
        {
          title: 'Phone Numbers',
          route: ROUTES.ADMIN.VOICE.PHONE_NUMBER_LIST
        },
        {
          title: 'Calls',
          route: ROUTES.ADMIN.VOICE.CALL_LIST
        }
      ]
    },
    // {
    //   icon: EnvelopeIcon,
    //   title: 'Email',
    //   routes: [
    //     {
    //       title: 'Reviews',
    //       route: ROUTES.ADMIN.EMAIL.REVIEW_LIST
    //     }
    //   ],
    // },
    {
      icon: DocumentIcon,
      title: 'Documents',
      routes: [
        {
          title: 'Documents',
          route: ROUTES.ADMIN.DOCUMENTS.DOCUMENTS_LIST
        },
        {
          title: 'Stale Documents',
          route: ROUTES.ADMIN.DOCUMENTS.STALE_DOCUMENTS
        }
      ]
    },
    {
      icon: CreditCardIcon,
      title: 'Payments',
      routes: [
        {
          title: 'Invoices',
          route: ROUTES.ADMIN.PAYMENTS.INVOICES_LIST
        }
      ],
      requiredPermission: 'is_payments_superuser'
    },
    {
      icon: QuestionMarkCircleIcon,
      title: 'Questionnaires',
      routes: [
        {
          title: 'Questionnaires',
          route: ROUTES.ADMIN.QUESTIONNAIRES.QUESTIONNAIRE_LIST
        }
      ]
    },
    {
      icon: DocumentTextIcon,
      title: 'Dokumentenvorlagen',
      routes: [
        {
          title: 'PDF Form Templates',
          route: ROUTES.ADMIN.PDF_FORM_TEMPLATES.PDF_FORM_TEMPLATES_LIST
        }
      ],
      requiredFeatureFlag: () => pdfFormTemplatesAdminFlag.isEnabled.value
    },
    {
      icon: EnvelopeIcon,
      title: 'Emails',
      routes: [
        {
          title: 'Emails',
          route: ROUTES.ADMIN.EMAIL.INBOUND_EMAIL_LIST
        }
      ]
    },
    {
      icon: StarIcon,
      title: 'Feedback',
      routes: [
        {
          title: 'Feedback',
          route: ROUTES.ADMIN.FEEDBACK.FEEDBACK_LIST
        }
      ]
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'Chats',
      routes: [
        {
          title: 'All Chats',
          route: ROUTES.ADMIN.CHATS.CHAT_LIST
        },
        {
          title: 'Search Chats',
          route: ROUTES.ADMIN.CHATS.CHAT_SEARCH
        }
      ]
    },
    {
      icon: PencilSquareIcon,
      title: 'Word Templates',
      routes: [
        {
          title: 'Review Templates',
          route: ROUTES.ADMIN.WORD_TEMPLATES.REVIEW
        },
        {
          title: 'Placeholders',
          route: ROUTES.ADMIN.WORD_TEMPLATES.PLACEHOLDERS
        }
      ]
    },
    {
      icon: SwatchIcon,
      title: 'UI Components',
      routes: [
        {
          title: 'Component Library',
          route: ROUTES.ADMIN.UI_COMPONENTS
        }
      ]
    }
  ]

  const availableTopics = computed<Topic[]>(() => {
    return topics.filter((topic) => {
      if (topic.requiredPermission) {
        // @ts-ignore
        return user.value?.[topic.requiredPermission]
      }
      if (topic.requiredFeatureFlag) {
        return topic.requiredFeatureFlag()
      }
      return true
    })
  })

  /**
   * The topic that is currently active. This is used to toggle the visibility of the routes
   * for the active topic.
   */
  const activeTopic = computed<Topic | undefined>(() => {
    /**
     * Find the topic object that matches the current route path, or the dynamic path.
     * The dynamic path is the route path without the last segment. This allows us to match
     * the parent topic when the current route is a child route.
     */
    // eslint-disable-next-line no-useless-escape
    const dynamicPath = route.path.replace(/\/[^\/]+$/, '')
    return topics.find((topic) => {
      return topic.routes.some((routeObj) => {
        return routeObj.route === route.path || routeObj.route === dynamicPath
      })
    })
  })

  /**
   * The topic that is currently selected. This is used to toggle the visibility of the routes
   * for the selected topic.
   */
  const selectedTopic = ref<Topic | null>(null)

  /**
   * The route that is currently active. This is used to highlight the active route in the nav.
   */
  const activeRoute = computed<Route | undefined>(() => {
    /**
     * Find the route object that matches the current route path, or the dynamic path.
     * The dynamic path is the route path without the last segment. This allows us to match
     * the parent route when the current route is a child route.
     */
    // eslint-disable-next-line no-useless-escape
    const dynamicPath = route.path.replace(/\/[^\/]+$/, '')
    for (const topic of topics) {
      const routeObj = topic.routes.find((routeObj) => routeObj.route === route.path)
      if (routeObj) {
        return routeObj
      } else {
        const dynamicRouteObj = topic.routes.find((routeObj) => routeObj.route === dynamicPath)
        if (dynamicRouteObj) {
          return dynamicRouteObj
        }
      }
    }
    return undefined
  })

  // Because we are using :to instead of href, we need to manually remove the backdrop when the route changes.
  const clearBackdrops = async (to: string) => {
    if (to === route.path) {
      return
    }
    await nextTick()
    const backdrops = document.querySelector('#admin-sidebar-backdrop')
    if (backdrops) {
      backdrops.remove()
    }
  }
</script>

<template>
  <nav class="flex w-full flex-col flex-wrap pb-3">
    <ul>
      <!-- Link -->
      <li class="mb-1.5 px-5">
        <router-link
          class="active-link flex gap-x-3 rounded-lg px-3 py-2 text-sm text-gray-800 hover:bg-gray-100
            focus:bg-gray-100 focus:outline-none"
          :to="ROUTES.ADMIN.DASHBOARD"
          @click="clearBackdrops(ROUTES.ADMIN.DASHBOARD)"
        >
          <HomeIcon class="mt-0.5 size-4 flex-shrink-0" />
          Dashboard
        </router-link>
      </li>
      <!-- End Link -->
      <li
        v-for="topic in availableTopics"
        :key="topic.title"
        class="mb-1.5 px-5"
      >
        <button
          type="button"
          :class="topic === activeTopic ? 'bg-gray-100' : ''"
          class="flex w-full gap-x-3 rounded-lg px-3 py-2 text-start text-sm text-gray-800 hover:bg-gray-100
            focus:outline-none disabled:pointer-events-none disabled:opacity-50"
          @click="selectedTopic = selectedTopic?.title === topic.title ? null : topic"
        >
          <component
            :is="topic.icon"
            class="mt-0.5 size-4 flex-shrink-0"
          />
          {{ topic.title }}
          <ChevronDownIcon
            v-if="activeTopic === topic || selectedTopic?.title === topic.title"
            class="ms-auto mt-1 size-3.5 flex-shrink-0 transition"
          />
          <ChevronLeftIcon
            v-else
            class="ms-auto mt-1 size-3.5 flex-shrink-0 transition"
          />
        </button>
        <div
          v-show="activeTopic === topic || selectedTopic?.title === topic.title"
          class="transition-[height] duration-300"
        >
          <ul
            class="relative mt-1.5 space-y-1.5 ps-7 before:absolute before:start-[18px] before:top-0 before:h-full
              before:w-0.5 before:bg-gray-100"
          >
            <li
              v-for="dashboard_route in topic.routes"
              :key="dashboard_route.route"
            >
              <router-link
                :to="dashboard_route.route"
                :class="dashboard_route === activeRoute ? 'bg-gray-100' : ''"
                class="flex gap-x-4 rounded-lg px-3 py-2 text-sm text-gray-800 hover:bg-gray-100 focus:bg-gray-100
                  focus:outline-none"
                @click="clearBackdrops(dashboard_route.route)"
              >
                {{ dashboard_route.title }}
              </router-link>
            </li>
          </ul>
        </div>
      </li>
    </ul>
  </nav>
</template>

<style scoped></style>
